import {
  Component,
  OnInit,
  OnDestroy,
  inject,
  ChangeDetectorRef,
  AfterViewInit,
  AfterViewChecked,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { AgGridAngular } from "ag-grid-angular";
import { ColDef, GridOptions, GridReadyEvent } from "ag-grid-community";
import { AllCommunityModule, ModuleRegistry } from "ag-grid-community";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { Store } from "@ngrx/store";

import { myCustomTheme } from "../../constants/grid-themes";
import { MipsFileRepositoryItem } from "@models/mips-file-repository.model";
import { MipsFileRepositoryActions } from "@store/actions/mips-file-repository/mips-file-repository.actions";
import {
  selectFiles,
  selectFilesLoading,
} from "@store/selectors/mips-file-repository/mips-file-repository.selectors";

ModuleRegistry.registerModules([AllCommunityModule]);

@Component({
  selector: "app-mips-file-repository",
  standalone: true,
  imports: [
    CommonModule,
    AgGridAngular,
  ],
  templateUrl: "./mips-file-repository.component.html",
  styleUrl: "./mips-file-repository.component.css",
})
export class MipsFileRepositoryComponent
  implements OnInit, AfterViewInit, AfterViewChecked, OnDestroy
{
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly destroy$ = new Subject<void>();

  theme = myCustomTheme;
  private gridApi: any;
  files: MipsFileRepositoryItem[] = [];

  rowData$ = this.store.select(selectFiles);
  loading$ = this.store.select(selectFilesLoading);
  isLoading = true;

  public gridOptions: GridOptions = {
    rowSelection: "single",
    pagination: true,
    paginationPageSize: 20,
    suppressClickEdit: true,
    loading: true,
    rowData: [],
    onGridReady: this.onGridReady.bind(this),
  };

  colDefs: ColDef[] = [
    {
      field: "organizationName",
      headerName: "Organization Name",
      flex: 2,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: "documentType",
      headerName: "Document Type",
      flex: 1,
      tooltipValueGetter: (p: any) => p.value,
      valueFormatter: (p: any) => p.value ? p.value.toUpperCase() : "",
    },
    {
      field: "entityType",
      headerName: "Entity Type",
      flex: 1,
      tooltipValueGetter: (p: any) => p.value,
      valueFormatter: (p: any) => this.capitalizeFirst(p.value),
    },
    {
      field: "program",
      headerName: "Program",
      flex: 1,
      tooltipValueGetter: (p: any) => p.value,
      valueFormatter: (p: any) => this.capitalizeFirst(p.value),
    },
    {
      field: "feedbackEmail",
      headerName: "Feedback Email",
      flex: 1.5,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: "fileName",
      headerName: "Document Name",
      flex: 1.5,
      tooltipValueGetter: (p: any) => p.value,
    },
    {
      field: "actions",
      headerName: "Actions",
      minWidth: 200,
      width: 200,
      pinned: "right",
      filter: false,
      sortable: false,
      resizable: false,
      cellRenderer: (params: any) => {
        return `<div style="display: flex; align-items: center; gap: 4px; height: 100%;">
          <button class="action-btn view-btn" data-action="view" title="View">
            <span class="material-icons" data-action="view" style="font-size: 18px;">visibility</span>
          </button>
          <button class="action-btn download-btn" data-action="download" title="Download">
            <span class="material-icons" data-action="download" style="font-size: 18px;">download</span>
          </button>
        </div>`;
      },
    },
  ];

  defaultColDef: ColDef = {
    flex: 1,
    filter: true,
    resizable: true,
    sortable: true,
    editable: false,
    minWidth: 100,
  };

  ngOnInit(): void {
    this.loading$.pipe(takeUntil(this.destroy$)).subscribe((loading) => {
      this.isLoading = loading;
      if (this.gridApi) {
        this.gridApi.setGridOption("loading", loading);
      }
    });

    this.rowData$.pipe(takeUntil(this.destroy$)).subscribe((files) => {
      this.files = files || [];
      if (this.gridApi && !this.isLoading) {
        this.gridApi.setGridOption("rowData", this.files);
      }
    });

    this.store.dispatch(MipsFileRepositoryActions.getFiles());
  }

  ngAfterViewInit(): void {
    this.cdr.detectChanges();
  }

  ngAfterViewChecked(): void {
    this.cdr.detectChanges();
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    this.gridApi.setGridOption("loading", this.isLoading);

    if (this.files.length > 0) {
      this.gridApi.setGridOption("rowData", this.files);
    }
  }

  onCellClicked(params: any): void {
    if (params.column.colId === "actions" && params.event.target.dataset.action) {
      const action = params.event.target.dataset.action;
      const data = params.data as MipsFileRepositoryItem;

      if (action === "view") {
        this.store.dispatch(
          MipsFileRepositoryActions.viewDocument({
            submissionId: data.submissionId,
            documentId: data.documentId,
            fileName: data.fileName,
          })
        );
      } else if (action === "download") {
        this.store.dispatch(
          MipsFileRepositoryActions.downloadDocument({
            submissionId: data.submissionId,
            documentId: data.documentId,
            fileName: data.fileName,
          })
        );
      }
    }
  }

  private capitalizeFirst(value: string): string {
    if (!value) return "";
    return value.charAt(0).toUpperCase() + value.slice(1);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
